import { friendlySdgAlignmentLevel } from '@upright/common/model/input/sdg-alignment/friendly';
import { SdgAlignmentStrength, SdgUnitsMode } from '@upright/common/model/output/scores/sdg-alignment/types';
import { toSdgAlignmentLevel } from '@upright/common/model/output/scores/sdg-alignment/utils';
import { tabletOrNarrower } from '@upright/ui/common/generic/layout/breakpoint-utils';
import { getSdgColor } from '@upright/ui/common/upright/model/output/scores/sdg-alignments/shared';
import styled, { css } from 'styled-components';
import { Side } from '../../../impact-lens-profile/common/layout/types';
import { ImpactLensLegendItem } from '../../shared/impact-lens-legend-item';
import { SdgLensBottomAreaLayout } from '../common/layout/bottom-area-layout';
import { sideToSdgValence } from '../common/utils';
import { useSdgUnitsMode } from '@upright/ui/common/upright/model/output/scores/sdg-alignments/use-sdg-unit-mode';

export const SdgLensLegendMaybe = (props: {
  legendAreaBoundsGetter: SdgLensBottomAreaLayout['getLegendAreaBounds'];
}) => {
  const [unitsMode] = useSdgUnitsMode();
  const shouldShowLegend = unitsMode === SdgUnitsMode.PURE_REVENUE_SHARES;

  if (!shouldShowLegend) return <></>;

  return (
    <LegendWrap>
      <SdgLegendOfSide side={Side.LEFT} getBounds={props.legendAreaBoundsGetter} />
      <SdgLegendOfSide side={Side.RIGHT} getBounds={props.legendAreaBoundsGetter} />
    </LegendWrap>
  );
};

const LegendWrap = styled.div`
  ${tabletOrNarrower(css`
    display: none;
  `)};
`;

const SdgLegendOfSide = (props: { side: Side; getBounds: SdgLensBottomAreaLayout['getLegendAreaBounds'] }) => {
  const valence = sideToSdgValence(props.side);
  const strengths = [SdgAlignmentStrength.STRONG, SdgAlignmentStrength.NORMAL, SdgAlignmentStrength.WEAK];

  return (
    <LegendContainer side={props.side} getBounds={props.getBounds}>
      {strengths.map(strength => (
        <ImpactLensLegendItem
          key={`legend-${valence}-${strength}`}
          text={friendlySdgAlignmentLevel(toSdgAlignmentLevel(valence, strength))}
          color={getSdgColor(valence, strength)}
        />
      ))}
    </LegendContainer>
  );
};

const LegendContainer = styled.div<{ side: Side; getBounds: SdgLensBottomAreaLayout['getLegendAreaBounds'] }>`
  position: absolute;
  ${props => {
    const bounds = props.getBounds(props.side);
    return css`
      top: ${bounds.y1}px;
      left: ${bounds.x1}px;
      width: ${bounds.width}px;
      height: ${bounds.height}px;
    `;
  }}

  display: flex;
  align-items: flex-start;

  ${props => {
    // In addition to being a space between the items, this margin also keeps the legend from hitting the outer edges of the lens area
    const marginBetweenItems = '15px';
    switch (props.side) {
      case Side.LEFT:
        return css`
          flex-direction: row-reverse;
          > * {
            margin-left: ${marginBetweenItems};
          }
        `;
      case Side.RIGHT:
        return css`
          > * {
            margin-right: ${marginBetweenItems};
          }
        `;
    }
  }}
`;
